/**
 * Pure view-model helpers for the Admin **Food ops consoles** (Food Ops interventions, onboarding review, ads
 * governance, fulfillment-config governance). These are platform control-plane surfaces: they bind to the live
 * `vello-api` surface through the typed `@vello/api-client` endpoint groups (operator AAL2 + scoped RBAC), and the
 * network + React live in `../web/screens/*`. Everything derivable from a response — status→tone mapping, lifecycle
 * gating, queue summaries, spend utilisation, and the no-loosening comparison of locked constraints — is a pure
 * function here so it is unit-tested in Node without a DOM (mirroring `ops-console`, `finance-cases`, …).
 *
 * No copy, no color, no country/currency literal lives here: tones are semantic names the screen maps to tokens,
 * money is decoded to the neutral `Money` value (minor units + ISO 4217) for `@vello/format` to render per locale.
 */
import { money, type Money } from '@vello/core';
import {
  type FoodOpsInterventionView,
  type FoodOpsInterventionStatus,
  type FoodReviewSubmissionView,
  type FoodReviewStatusView,
  type FoodAdCampaignLedgerView,
  type FoodAdMoneyView,
  type FoodAdKillSwitchView,
  type FoodFulfillmentLockedView,
} from '@vello/api-client';

/** Semantic tone the screen maps to a `@vello/tokens` color role (never a literal color). */
export type ConsoleTone = 'neutral' | 'success' | 'danger' | 'warning';

// ---- money on the wire --------------------------------------------------------------------------------

/** Decode a paired wire-money `{ amountMinor, currency }` to a domain `Money` (minor units as an exact bigint). */
export function moneyFromWire(m: FoodAdMoneyView): Money {
  return money(BigInt(m.amountMinor), m.currency);
}

/** Decode a bare minor-unit string against a shared ISO 4217 currency to a domain `Money`. */
export function minorFromWire(amountMinor: string, currency: string): Money {
  return money(BigInt(amountMinor), currency);
}

// ---- Food Ops interventions ---------------------------------------------------------------------------

/** Tone for an intervention status: open→triage (warning), acknowledged→in hand (neutral), escalated→severe
 *  (danger), resolved→closed (success). */
export function interventionTone(status: FoodOpsInterventionStatus): ConsoleTone {
  switch (status) {
    case 'open':
      return 'warning';
    case 'acknowledged':
      return 'neutral';
    case 'escalated':
      return 'danger';
    case 'resolved':
      return 'success';
  }
}

/** Which lifecycle transitions the UI offers from a status. The server is authoritative (an invalid transition
 *  is refused); this only gates which buttons render, following open → acknowledge → escalate → resolve. */
export interface InterventionActions {
  readonly canAcknowledge: boolean;
  readonly canEscalate: boolean;
  readonly canResolve: boolean;
}

export function interventionActions(status: FoodOpsInterventionStatus): InterventionActions {
  switch (status) {
    case 'open':
      return { canAcknowledge: true, canEscalate: true, canResolve: true };
    case 'acknowledged':
      return { canAcknowledge: false, canEscalate: true, canResolve: true };
    case 'escalated':
      return { canAcknowledge: false, canEscalate: false, canResolve: true };
    case 'resolved':
      return { canAcknowledge: false, canEscalate: false, canResolve: false };
  }
}

/** Whether an intervention is still actionable (anything but resolved). */
export function interventionIsOpen(status: FoodOpsInterventionStatus): boolean {
  return status !== 'resolved';
}

/** Split a mixed list into the actionable set and the resolved set, each preserving input order. */
export function partitionInterventions(list: readonly FoodOpsInterventionView[]): {
  readonly open: readonly FoodOpsInterventionView[];
  readonly resolved: readonly FoodOpsInterventionView[];
} {
  const open: FoodOpsInterventionView[] = [];
  const resolved: FoodOpsInterventionView[] = [];
  for (const i of list) (interventionIsOpen(i.status) ? open : resolved).push(i);
  return { open, resolved };
}

// ---- onboarding review --------------------------------------------------------------------------------

/** Tone for a review status. Terminal-approved is success, rejected is danger; the in-flight states that want a
 *  reviewer's attention are warning/neutral. */
export function reviewTone(status: FoodReviewStatusView): ConsoleTone {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    case 'changes_requested':
    case 'appealed':
    case 'awaiting_second_approval':
      return 'warning';
    case 'pending':
    case 'in_review':
      return 'neutral';
  }
}

/** Whether a submission still needs reviewer action (everything but the terminal approved/rejected outcomes). */
export function reviewNeedsAction(status: FoodReviewStatusView): boolean {
  return status !== 'approved' && status !== 'rejected';
}

/** A review decision the console can offer from a status. The server is authoritative on whether it is actually
 *  permitted (e.g. four-eyes: the second approver must differ; an appeal resolver must differ from the rejecter). */
export type ReviewAction = 'claim' | 'approve' | 'request_changes' | 'reject' | 'resubmit' | 'appeal';

/** The decisions offered from a status, most-prominent first (the screen renders the first as the one primary). */
export function reviewAvailableActions(status: FoodReviewStatusView): readonly ReviewAction[] {
  switch (status) {
    case 'pending':
      return ['claim'];
    case 'in_review':
      return ['approve', 'request_changes', 'reject'];
    case 'awaiting_second_approval':
      return ['approve', 'reject'];
    case 'changes_requested':
      return ['resubmit'];
    case 'rejected':
    case 'appealed':
      return ['appeal'];
    case 'approved':
      return [];
  }
}

/** A count summary of a reviewer queue, for the header stat tiles. */
export interface ReviewQueueSummary {
  readonly total: number;
  readonly needsAction: number;
  readonly pending: number;
  readonly inReview: number;
  readonly awaitingSecond: number;
  readonly changesRequested: number;
  readonly appealed: number;
}

export function reviewQueueSummary(subs: readonly FoodReviewSubmissionView[]): ReviewQueueSummary {
  let needsAction = 0;
  let pending = 0;
  let inReview = 0;
  let awaitingSecond = 0;
  let changesRequested = 0;
  let appealed = 0;
  for (const s of subs) {
    if (reviewNeedsAction(s.status)) needsAction++;
    if (s.status === 'pending') pending++;
    else if (s.status === 'in_review') inReview++;
    else if (s.status === 'awaiting_second_approval') awaitingSecond++;
    else if (s.status === 'changes_requested') changesRequested++;
    else if (s.status === 'appealed') appealed++;
  }
  return { total: subs.length, needsAction, pending, inReview, awaitingSecond, changesRequested, appealed };
}

// ---- ads governance -----------------------------------------------------------------------------------

/** Spend utilisation for a sponsored-campaign ledger, computed in exact minor units then reduced to a
 *  display percentage (0–100, clamped) and the remaining headroom as `Money`. */
export interface CampaignSpend {
  readonly currency: string;
  readonly budgetPct: number;
  readonly capPct: number;
  readonly budgetRemaining: Money;
  readonly capRemaining: Money;
}

/** Percentage (2-decimal) of `spent` against `total`, in exact bigint math, clamped to [0, 100+]; 0 when the
 *  denominator is 0. Reported values can exceed 100 only if an invariant broke upstream — surfaced, not hidden. */
function pctOf(spent: bigint, total: bigint): number {
  if (total <= 0n) return 0;
  return Number((spent * 10000n) / total) / 100;
}

function remaining(total: bigint, spent: bigint): bigint {
  const left = total - spent;
  return left > 0n ? left : 0n;
}

export function campaignSpend(ledger: FoodAdCampaignLedgerView): CampaignSpend {
  const currency = ledger.budget.budget.currency;
  const budget = BigInt(ledger.budget.budget.amountMinor);
  const spent = BigInt(ledger.budget.spent.amountMinor);
  const cap = BigInt(ledger.controls.spendCap.amountMinor);
  const capSpent = BigInt(ledger.controls.spent.amountMinor);
  return {
    currency,
    budgetPct: pctOf(spent, budget),
    capPct: pctOf(capSpent, cap),
    budgetRemaining: money(remaining(budget, spent), currency),
    capRemaining: money(remaining(cap, capSpent), ledger.controls.spendCap.currency),
  };
}

/** Tone for the kill-switch posture: engaged (charges blocked) is danger; released (operating) is success. */
export function killSwitchTone(k: FoodAdKillSwitchView): ConsoleTone {
  return k.engaged ? 'danger' : 'success';
}

// ---- fulfillment-config: the no-loosening guard -------------------------------------------------------

/** How a candidate's locked constraint moved relative to a baseline. `loosened` is the illegal direction the
 *  server refuses; `tightened`/`unchanged` are allowed. */
export type LockedDirection = 'tightened' | 'unchanged' | 'loosened';

export interface LockedComparisonRow {
  /** Stable key identifying the constraint (`radius` | `prohibited` | `orderCap`), for copy + testing. */
  readonly key: 'radius' | 'prohibited' | 'orderCap';
  readonly direction: LockedDirection;
  /** For the prohibited-categories row: categories present in the baseline but dropped by the candidate
   *  (a removed prohibition = a loosening); empty for the other rows. */
  readonly removedCategories: readonly string[];
  /** For the prohibited-categories row: categories the candidate added (a tightening); empty otherwise. */
  readonly addedCategories: readonly string[];
}

export interface LockedComparison {
  readonly rows: readonly LockedComparisonRow[];
  /** True if ANY locked constraint loosened below the baseline — the condition the publish guard refuses (409). */
  readonly loosens: boolean;
}

/**
 * Compare a candidate policy's locked legal/safety constraints against a baseline (a higher scope, or the prior
 * active version). A larger delivery radius, a dropped prohibited category, or a larger/removed order-value cap
 * are all *loosenings*; the inverse are *tightenings*. This mirrors the server's no-illegal-loosening guard so the
 * console can show, per version, whether it tightened / left / loosened the locked block — and warn before a
 * publish the server would reject.
 */
export function compareLocked(baseline: FoodFulfillmentLockedView, candidate: FoodFulfillmentLockedView): LockedComparison {
  const rows: LockedComparisonRow[] = [];

  // Delivery radius: bigger = more permissive = loosened.
  rows.push({
    key: 'radius',
    direction:
      candidate.maxDeliveryRadiusMeters > baseline.maxDeliveryRadiusMeters
        ? 'loosened'
        : candidate.maxDeliveryRadiusMeters < baseline.maxDeliveryRadiusMeters
          ? 'tightened'
          : 'unchanged',
    removedCategories: [],
    addedCategories: [],
  });

  // Prohibited categories: dropping a prohibition = loosened; adding one = tightened.
  const baseSet = new Set(baseline.prohibitedItemCategories);
  const candSet = new Set(candidate.prohibitedItemCategories);
  const removed = [...baseSet].filter((c) => !candSet.has(c));
  const added = [...candSet].filter((c) => !baseSet.has(c));
  rows.push({
    key: 'prohibited',
    direction: removed.length > 0 ? 'loosened' : added.length > 0 ? 'tightened' : 'unchanged',
    removedCategories: removed,
    addedCategories: added,
  });

  // Order-value cap: a higher cap, or removing the cap entirely, is a loosening; a lower or newly-added cap tightens.
  rows.push({
    key: 'orderCap',
    direction: orderCapDirection(baseline.maxOrderValueMinor, candidate.maxOrderValueMinor),
    removedCategories: [],
    addedCategories: [],
  });

  return { rows, loosens: rows.some((row) => row.direction === 'loosened') };
}

function orderCapDirection(baseline: string | undefined, candidate: string | undefined): LockedDirection {
  if (baseline === undefined && candidate === undefined) return 'unchanged';
  if (baseline === undefined) return 'tightened'; // adding a cap where there was none tightens
  if (candidate === undefined) return 'loosened'; // removing the cap loosens (now uncapped)
  const b = BigInt(baseline);
  const c = BigInt(candidate);
  return c > b ? 'loosened' : c < b ? 'tightened' : 'unchanged';
}

/** The most-permissive movement across a set of locked rows: any loosening dominates, else any tightening. */
export function worstDirection(dirs: readonly LockedDirection[]): LockedDirection {
  if (dirs.includes('loosened')) return 'loosened';
  if (dirs.includes('tightened')) return 'tightened';
  return 'unchanged';
}
